/**
 * **추적 등록 창의 판단** — 어느 갈래로 여나 · 그 갈래가 어느 칸을 쓰나 · 플랜이 주나 · 알림 기본값 ·
 * 걸면 생기는 것 · 등록 뒤 어디로 가나.
 *
 * ```
 *   갈래(탭)               쓰는 칸                     등록 뒤 가는 곳
 *   우리 브랜드 키워드      brand.brandKeywordCount      브랜드 멘션 추적(1년 · 띠)
 *   키워드·경쟁 브랜드      keyword.keywordCount         그 세트 화면(1년 · 띠)
 *   계정 · 우리 계정(자사)  brandAccount.brandAccountCount 자사 계정 관리
 *   계정 · 남의 계정        keyword.keywordCount         그 세트 화면(띠 · 1년 약속 없음)
 * ```
 *
 * ⛔ **「강화 수집」·광고·판매몰 상품을 약속하지 마라** — 세트는 소셜 글만 모은다.
 * ⚠️ **계정 갈래는 「과거 1년치」를 적지 않는다** — 계정 글을 거슬러 모으는 범위를 적은 계약이 없다.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 함수다.
 */

import { paths } from 'src/routes/paths';

import { fNumber } from 'src/utils/format/format-number';
import { TRACK_AREA_NAME } from 'src/utils/plan/track-capacity';

import { IPlanService } from 'src/constants/plan-service';

import { toTrackLandingHref } from './track-landing';

/** 창의 탭 — 무엇을 거는가 */
export type TrackRegisterType = 'brand' | 'keyword' | 'account';

/** 계정 갈래 안의 두 길 — 우리 계정(자사 계정 칸)이냐, 남의 계정(세트에 담기)이냐 */
export type TrackAccountOwner = 'own' | 'other';

/** 세트에 거는 갈래의 두 길 — 새 세트를 세우나, 있는 세트에 더하나 */
export type TrackGroupMode = 'new' | 'existing';

/** 갈래가 쓰는 플랜 칸 */
export type TrackSlotKind = 'brandKeyword' | 'brandAccount' | 'targetKeyword';

/** 부르는 화면이 넘기는 값 — 비어 있으면 창에서 적는다 */
export type TrackRegisterInitial = {
  /** 부르는 화면이 아는 갈래 — 없으면 값으로 고른다(`pickDefaultTrackType`) */
  type?: TrackRegisterType;
  /** 말 — 브랜드 이름 · 키워드 */
  value?: string;
  /** 계정의 채널 */
  platform?: string;
  /** 계정 아이디(`@` 없이) */
  username?: string;
  /** 계정 갈래의 길 — 없으면 남의 계정 */
  accountOwner?: TrackAccountOwner;
};

/**
 * 알림 칸 — 매일 이메일 · 매주 이메일 · 매월 이메일 · Slack 실시간.
 * `monthly` 는 알림 설정 창만 다룬다 — 추적 등록 창·온보딩은 싣지 않고, 빠지면 그 칸을 건드리지 않는다.
 */
export type TrackAlerts = { daily: boolean; weekly: boolean; monthly?: boolean; slack: boolean };

/** 탭 이름 — 창의 `SegmentTabs` 차례 그대로 */
export const TRACK_TYPE_TABS: { key: TrackRegisterType; label: string; shortLabel?: string }[] = [
  { key: 'brand', label: '우리 브랜드 키워드', shortLabel: '우리 브랜드' },
  { key: 'keyword', label: '키워드·경쟁 브랜드', shortLabel: '키워드' },
  { key: 'account', label: '계정' },
];

/** 계정 알림이 없는 자리의 한 줄(A659 — 알림 대상 종류에 계정이 없다) */
export const TRACK_ACCOUNT_ALERT_NOTE = '계정 알림은 아직 지원하지 않습니다';

/** 약속의 뒤 절 — 추적이 살아 있는 동안만이다(워크스페이스가 만료되면 멈추므로 「영원히」를 적지 않는다) */
const TRACK_KEEP_CLAUSE = '앞으로 새로 모이는 글도 추적하는 동안 계속 쌓여요';

/**
 * 추적의 약속 한 줄 — 조각으로 준다. 수와 단위(`count`)는 그리는 쪽이 줄바꿈 없이 붙인다.
 *
 * 「추적하면 최근 1년간 1,234건을 바로 볼 수 있고, 앞으로 새로 모이는 글도 추적하는 동안 계속 쌓여요.」
 * 수를 모르면(조회 전·실패·0건) 「추적하면 최근 1년치를 바로 볼 수 있고, …」 — 수 없이.
 * ⚠️ 말 갈래(우리 브랜드 키워드 · 키워드)만의 약속이다 — 계정은 `TRACK_ACCOUNT_PROMISE`.
 *
 * @param count 최근 1년 건수 — 추적 화면과 같은 질의로 센 수만 넘긴다
 * @param lead 앞머리 — 「‘아이패치’ 언급을 추적하면」처럼 바꿔 끼운다
 */
export function toTrackPromise(
  count: number | null | undefined,
  lead = '추적하면',
): { before: string; count: string | null; after: string } {
  if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
    return { before: `${lead} 최근 1년간 `, count: `${fNumber(count)}건`, after: `을 바로 볼 수 있고, ${TRACK_KEEP_CLAUSE}.` };
  }
  return { before: `${lead} 최근 1년치를 바로 볼 수 있고, ${TRACK_KEEP_CLAUSE}.`, count: null, after: '' };
}

/** `toTrackPromise` 를 한 줄 글로 */
export function toTrackPromiseText(count: number | null | undefined, lead?: string): string {
  const { before, count: n, after } = toTrackPromise(count, lead);
  return `${before}${n ?? ''}${after}`;
}

/** 계정 갈래의 약속 — 1년을 적지 않는다(머리말의 ⚠️) */
export const TRACK_ACCOUNT_PROMISE = `추적하면 이 계정의 새 글과 언급이 추적하는 동안 계속 쌓여요.`;

/** 1년 약속이 없는 자리(계정 · 기간을 안 맞춘 화면)의 뒤 절만 */
export const TRACK_KEEP_LINE = '앞으로 새로 모이는 글이 추적하는 동안 계속 쌓여요.';

/**
 * 추적 등록 창의 요약 카드 — 갈래마다 큰 줄(`head`) · 이름표(`label`) · 아래 작은 줄(`foot`).
 * 수가 있으면 큰 줄 자리에 수가 서고(`label` 이 그 수의 이름), 없으면 `head` 가 선다.
 * ⚠️ 우리 브랜드 키워드는 수를 적지 않는다 · 계정은 1년을 적지 않는다(머리말의 ⚠️).
 */
export const TRACK_YEAR_CARD = {
  label: '추적하면 바로 볼 수 있는 최근 1년 글',
  foot: `${TRACK_KEEP_CLAUSE}.`,
  wordHead: '최근 1년치를 바로 볼 수 있어요',
  accountHead: '이 계정의 새 글과 언급을 모아요',
  accountFoot: '추적하는 동안 계속 쌓여요.',
} as const;

/**
 * 일자별 지도(`YYYYMMDD` → 건수) → 끝 날 **앞 달까지 다 지난 12달**의 달별 합.
 * ⚠️ 이번 달은 넣지 않는다 — 며칠치뿐이라 마지막 막대가 「줄었다」로 읽힌다.
 * 비어 있는 달도 0 으로 세운다 — 빠뜨리면 「그 달이 없다」와 「0건」이 같아 보인다.
 *
 * @param daily `collection.dailyCount` 의 `daily`
 * @param end 기간의 끝 날
 * @returns 오래된 달 → 최근 달 · `month` 는 `YYYY-MM`
 */
export function toTrackYearMonths(
  daily: Record<string, number> | null | undefined,
  end: Date,
): { month: string; count: number }[] {
  const months: { month: string; count: number }[] = [];
  for (let i = 12; i >= 1; i -= 1) {
    const d = new Date(end.getFullYear(), end.getMonth() - i, 1);
    months.push({ month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, count: 0 });
  }
  const index = new Map(months.map((row, i) => [row.month.replace('-', ''), i]));
  Object.entries(daily ?? {}).forEach(([day, value]) => {
    const at = index.get(String(day).slice(0, 6));
    if (at !== undefined) months[at].count += Number(value) || 0;
  });
  return months;
}

/**
 * 약속 줄 아래에 더 적는 것 — 갈래마다.
 * 🚨 계약에 없는 말을 더하지 마라(머리말의 ⛔ · ⚠️).
 */
export const TRACK_BENEFITS: Record<TrackRegisterType, { icon: string; text: string }[]> = {
  brand: [{ icon: 'solar:bell-bing-linear', text: '이메일·Slack 알림을 받을 수 있습니다' }],
  keyword: [{ icon: 'solar:bell-bing-linear', text: '이메일·Slack 알림을 받을 수 있습니다' }],
  account: [],
};

/**
 * 창을 어느 탭으로 여나.
 *
 * 차례: 계정 값만 왔으면 계정 → **값이 우리 브랜드면 우리 브랜드 키워드**(브랜드 키워드 · 워크스페이스
 * 이름과 대소문자 없이 견준다) → 부르는 화면이 준 갈래 → 키워드.
 * ⚠️ 플랜이 키워드 칸을 안 줘도 탭을 바꾸지 않는다 — 그 탭에서 플랜 안내를 정직하게 한다.
 *
 * @param initial 부르는 화면이 넘긴 값
 * @param own 워크스페이스의 브랜드 키워드와 이름
 */
export function pickDefaultTrackType(
  initial: TrackRegisterInitial,
  own: { brandKeywords: readonly string[]; workspaceName?: string },
): TrackRegisterType {
  const value = norm(initial.value);
  if (initial.type === 'account' || (!value && norm(initial.username))) return 'account';
  if (value) {
    const mine = [...own.brandKeywords, own.workspaceName ?? ''].map(norm).filter(Boolean);
    if (mine.includes(value)) return 'brand';
  }
  return initial.type ?? 'keyword';
}

/** 갈래가 쓰는 칸 — 남의 계정은 세트에 담기므로 타겟 키워드 칸이다 */
export function trackSlotKind(type: TrackRegisterType, owner: TrackAccountOwner): TrackSlotKind {
  if (type === 'brand') return 'brandKeyword';
  if (type === 'account' && owner === 'own') return 'brandAccount';
  return 'targetKeyword';
}

/** 칸마다 플랜 표 필드 · 「…부터 제공됩니다」의 주어 — 칸 이름은 `TRACK_AREA_NAME`(추적 칸 한 벌) */
export const TRACK_SLOTS: Record<TrackSlotKind, { fieldKey: string; subject: string }> = {
  brandKeyword: { fieldKey: IPlanService.brand.brandKeywordCount, subject: '브랜드 키워드는' },
  brandAccount: { fieldKey: IPlanService.brandAccount.brandAccountCount, subject: '자사 소셜 계정은' },
  targetKeyword: { fieldKey: IPlanService.keyword.keywordCount, subject: '타겟 키워드는' },
};

/** 플랜 한도 값 → 수. 숫자가 아니면 `null`(모르는 것과 0 은 다르다 — 0 은 «그 칸이 없다») */
export function toTrackLimit(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * 칸 형편 — `locked` 는 플랜이 그 갈래를 아예 안 준다(한도 0), `full` 은 이번 등록이 한도를 넘는다.
 * @param limit 플랜 한도 — 모르면 `null`(그때는 막지 않는다)
 */
export function toTrackPlanState(limit: number | null, used: number, adding: number): 'locked' | 'full' | 'ok' {
  if (limit === 0) return 'locked';
  if (limit !== null && used + adding > limit) return 'full';
  return 'ok';
}

/** 칸이 다 찼을 때의 한 줄 — 「칸이 다 찼어요(3/3)」. 한도를 모르면 이 줄을 안 쓴다 */
export function toTrackSlotFullLine(used: number, limit: number | null): string {
  return limit === null ? '' : `칸이 다 찼어요(${fNumber(used)}/${fNumber(limit)})`;
}

/**
 * 칸 줄 — 「타겟 키워드 칸 7 / 10 사용 · 이번 등록 +1」.
 * 한도를 모르면 「타겟 키워드 칸 7개 사용 · 이번 등록 +1」.
 */
export function toTrackSlotLine(slot: TrackSlotKind, used: number, limit: number | null, adding: number): string {
  const usage = limit === null ? `${fNumber(used)}개 사용` : `${fNumber(used)} / ${fNumber(limit)} 사용`;
  return `${TRACK_AREA_NAME[slot]} ${usage} · 이번 등록 +${fNumber(adding)}`;
}

/**
 * 알림 칸의 처음 값.
 *
 * - 우리 브랜드 키워드: **워크스페이스의 브랜드 멘션 알림을 그대로 읽는다** — 그 알림은 브랜드 키워드 전체에
 *   한 벌이라, 창에서 바꾸면 전체가 바뀐다. 매일 이메일은 저장값이 없으면 켬(알림 설정 화면과 같다) ·
 *   Slack 은 연결돼 있을 때만 켬일 수 있다.
 * - 키워드(새 세트): 매일 이메일 켬 · 매주 끔 · Slack 끔(세트 만들기의 서버 기본과 같다).
 * - 계정: 전부 끔 — 계정 알림이 없다(`TRACK_ACCOUNT_ALERT_NOTE`).
 */
export function defaultTrackAlerts(
  type: TrackRegisterType,
  brandMention:
    | { messageEmail?: unknown; messageEmailWeekly?: unknown; messageEmailMonthly?: unknown; messageSlackRealTime?: unknown }
    | null
    | undefined,
  slackConnected: boolean,
): TrackAlerts {
  if (type === 'account') return { daily: false, weekly: false, slack: false };
  if (type === 'keyword') return { daily: true, weekly: false, slack: false };
  return {
    daily: brandMention?.messageEmail !== false,
    weekly: brandMention?.messageEmailWeekly === true,
    monthly: brandMention?.messageEmailMonthly === true,
    slack: slackConnected && brandMention?.messageSlackRealTime === true,
  };
}

/**
 * 알림 칸에 무엇을 그리나.
 *
 * `locked` 플랜이 그 갈래를 안 준다 · `account` 계정(알림 없음) · `group` 있는 세트에 더한다(그 세트의
 * 알림을 따른다) · `brand` 브랜드 멘션 알림(Slack 칸 있음) · `keyword` 새 세트(Slack 은 설정에서).
 */
export function toTrackAlertSection(
  type: TrackRegisterType,
  groupMode: TrackGroupMode,
  owner: TrackAccountOwner,
  locked: boolean,
): 'locked' | 'account' | 'group' | 'brand' | 'keyword' {
  if (locked) return 'locked';
  if (type === 'account') return 'account';
  if (type === 'brand') return 'brand';
  return groupMode === 'existing' && owner !== 'own' ? 'group' : 'keyword';
}

/**
 * 브랜드 멘션 알림의 바뀐 칸만 — `workspace.fields.update` 의 점 경로 이름으로.
 * Slack 은 이어져 있을 때만 켤 수 있다 · `explicit` 이면 매일·매주 이메일 두 칸은 늘 적는다 ·
 * 매월은 `next.monthly` 를 실었을 때만 본다.
 */
export function toBrandMentionAlertPatch(
  current:
    | { messageEmail?: unknown; messageEmailWeekly?: unknown; messageEmailMonthly?: unknown; messageSlackRealTime?: unknown }
    | null
    | undefined,
  next: TrackAlerts,
  slackConnected: boolean,
  explicit = false,
): Record<string, boolean> {
  const now = {
    daily: current?.messageEmail !== false,
    weekly: current?.messageEmailWeekly === true,
    slack: current?.messageSlackRealTime === true,
  };
  const want = { ...next, slack: slackConnected && next.slack };
  const patch: Record<string, boolean> = {};
  if (explicit || want.daily !== now.daily) patch['messageSettings.brandMention.messageEmail'] = want.daily;
  if (explicit || want.weekly !== now.weekly) patch['messageSettings.brandMention.messageEmailWeekly'] = want.weekly;
  if (next.monthly !== undefined && next.monthly !== (current?.messageEmailMonthly === true)) {
    patch['messageSettings.brandMention.messageEmailMonthly'] = next.monthly;
  }
  if (want.slack !== now.slack) patch['messageSettings.brandMention.messageSlackRealTime'] = want.slack;
  return patch;
}

/** 알림 한 줄 — 「매일 이메일 · Slack」 · 다 꺼졌으면 「꺼짐」 */
export function toTrackAlertLine(alerts: Partial<TrackAlerts> | null | undefined): string {
  const on = [
    alerts?.daily ? '매일 이메일' : '',
    alerts?.weekly ? '매주 이메일' : '',
    alerts?.monthly ? '매월 이메일' : '',
    alerts?.slack ? 'Slack' : '',
  ].filter(Boolean);
  return on.length > 0 ? on.join(' · ') : '꺼짐';
}

/** 등록이 끝난 것 — 어디로 보낼지 정하는 데 쓴다 */
export type TrackRegistered = {
  type: TrackRegisterType;
  owner: TrackAccountOwner;
  /** 걸린 말 또는 계정 아이디 */
  value: string;
  /** 세트에 걸었으면 그 세트 */
  campaignId?: string;
  /** 켜 둔 알림 — 띠가 적는다 */
  alerts?: TrackAlerts;
};

/**
 * 등록 뒤 가는 곳 — 걸린 것의 화면.
 *
 * 브랜드 키워드 → `/service/tracking/brand-mentions?tracked=1&period=365…` · 세트 → `/service/tracking/target-mentions/{id}?…`
 * (키워드는 `keyword=` 로 그 말을 고른 채 · 계정은 1년 약속이 없어 `period` 를 안 싣는다) ·
 * 우리 계정 → `/service/operations/accounts`. 세트 id 를 모르면 `null` — 부르는 쪽은 그 자리에 머문다.
 */
export function toTrackDestination(done: TrackRegistered): string | null {
  if (done.type === 'brand') {
    return toTrackLandingHref(paths.service.brand, { name: done.value, period: 365, alerts: done.alerts });
  }
  if (done.type === 'account' && done.owner === 'own') return paths.service.account;
  if (!done.campaignId) return null;
  const base = `${paths.service.keywordMonitoring}/${encodeURIComponent(done.campaignId)}`;
  if (done.type === 'account') return toTrackLandingHref(base, { name: `@${done.value}` });
  return toTrackLandingHref(base, { keyword: done.value, period: 365, alerts: done.alerts });
}

/** 비교용 — 다듬고 소문자 */
function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}
